import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import {
  AvatarGroup,
  AvatarGroupItem,
  Button,
  Caption1Strong,
  CounterBadge,
  Divider,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Persona,
  SearchBox,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens
} from "@fluentui/react-components";
import {
  Dismiss24Regular,
  ChatMultiple20Filled,
  ClockFilled,
  Mic20Filled,
  MicOff20Filled,
  ShareScreenStart20Filled,
  Speaker220Filled,
  SpeakerMute20Filled,
  Settings20Filled,
  Video20Filled,
  VideoOff20Filled
} from "@fluentui/react-icons";
import { FormattedMessage, useIntl } from "react-intl";
import { useRole } from "./useRole";
import { useSocialContext } from "../contexts/SocialContext";
import useBreakpoints from "../hooks/useBreakpoints";
import configManager from "../../singleton/ConfigManager";
import { useAllAvatarVolume } from "./useAvatarVolume";
import { MediaDevices } from "../../utils/media-devices-utils";
import { useParticipantLayoutDialogMediaState } from "../layout/participant-layout/useParticipantLayoutDialogMediaState";
import { ReactComponent as ShareScreenOffIcon } from "../icons/ShareScreenOff.svg";
import {
  createParticipantChatStatusByUserId,
  createAgentSearchResult,
  createParticipantSearchResult,
  getVirtualListWindow,
  normalizeParticipantSearchQuery,
  PARTICIPANT_SEARCH_DEBOUNCE_MS,
  shouldShowAgentChat
} from "./fluent-participants-overlay-utils";
import { canShowParticipantDialogChat } from "../layout/chat-visibility-utils";
import { isWebRtcDisabled } from "../../utils/room-entry-admission";
import { ResponsiveOverlayDrawer } from "../drawer/ResponsiveOverlayDrawer";

const PARTICIPANT_AVATAR_GROUP_PREVIEW_LIMIT = 8;
const PARTICIPANT_ROW_HEIGHT = 48;
const PARTICIPANT_SECTION_HEADER_HEIGHT = 36;
const PARTICIPANTS_DRAWER_EXIT_MS = 250;
const PARTICIPANT_DIVIDER_HEIGHT = 16;
const PARTICIPANT_LIST_OVERSCAN = 5;
const PARTICIPANT_LIST_DEFAULT_VIEWPORT_HEIGHT = 480;

const participantPropType = PropTypes.shape({
  id: PropTypes.string,
  isMe: PropTypes.bool,
  micPresence: PropTypes.shape({
    muted: PropTypes.bool
  }),
  permissions: PropTypes.object,
  presence: PropTypes.string,
  profile: PropTypes.shape({
    displayName: PropTypes.string,
    thumbnail: PropTypes.string
  }),
  roles: PropTypes.shape({
    owner: PropTypes.bool
  })
});

const agentPropType = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  provider: PropTypes.string
});

const useStyles = makeStyles({
  drawer: {
    width: "400px"
  },
  splitOverlay: {
    bottom: "50%!important",
    boxShadow: "none"
  },
  headerDrawer: {
    display: " flex",
    padding: " var(--Font-size-600, 24px) 16px 12px var(--Font-size-600, 24px)",
    alignItems: " flex-start",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: tokens.colorNeutralBackground1
  },
  drawerBody: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    minHeight: 0,
    overflowX: "hidden",
    overflowY: "auto",
    paddingTop: "20px"
  },
  search: {
    backgroundColor: "rgba(255, 255, 255, 0.10)",
    flexShrink: 0,
    width: "100%"
  },
  contentParticipants: {
    flexShrink: 0,
    position: "relative",
    width: "100%"
  },
  virtualListSpacer: {
    position: "relative",
    width: "100%"
  },
  virtualRow: {
    boxSizing: "border-box",
    left: 0,
    position: "absolute",
    right: 0,
    width: "100%"
  },
  section: {
    display: " flex",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "2px"
  },
  sectionNotFound: {
    alignItems: "center",
    display: "flex",
    height: `${PARTICIPANT_SECTION_HEADER_HEIGHT}px`,
    width: "100%"
  },
  sectionHeader: {
    boxSizing: "border-box",
    display: " flex",
    height: `${PARTICIPANT_SECTION_HEADER_HEIGHT}px`,
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--Gray-3, #828282)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "32px",
    "& .fui-Avatar__image": {
      width: "32px",
      height: "32px"
    },
    "& .fui-Avatar": {
      height: "32px"
    },
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "32px"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  mediaModerationButton: {
    width: "20px",
    height: "20px",
    minWidth: "20px",
    padding: 0,
    border: 0,
    backgroundColor: "transparent",
    color: "inherit",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      width: "20px",
      height: "20px"
    }
  },
  mediaModerationButtonDisabled: {
    cursor: "default",
    opacity: 1
  },
  divider: {
    boxSizing: "border-box",
    height: `${PARTICIPANT_DIVIDER_HEIGHT}px`,
    padding: "5px 0px 4px 0px"
  },
  footerParticipants: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: tokens.colorNeutralBackground1
  },
  redIcon: {
    color: "var(--Red, #EB5757)"
  },
  avatarGroupItem: {
    backgroundColor: "#333",
    "& img": {
      backgroundColor: "#333"
    }
  },
  avatarGroupItemAll: {
    "& img": {
      backgroundColor: "#333"
    }
  }
});

function canParticipantShareCamera(person) {
  return person?.permissions?.spawn_camera !== false;
}

function canParticipantShareScreen(person) {
  return person?.permissions?.spawn_and_move_media !== false;
}

export default function FluentParticipantOverlay({ isOpen, ...props }) {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [drawerOpen, setDrawerOpen] = useState(isOpen);

  useEffect(() => {
    let timeoutId;

    if (isOpen) {
      setShouldRender(true);
      timeoutId = setTimeout(() => setDrawerOpen(true), 0);
    } else {
      setDrawerOpen(false);
      timeoutId = setTimeout(() => setShouldRender(false), PARTICIPANTS_DRAWER_EXIT_MS);
    }

    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  if (!shouldRender) {
    return null;
  }

  return <FluentParticipantOverlayContent isOpen={drawerOpen} {...props} />;
}

FluentParticipantOverlay.propTypes = {
  isOpen: PropTypes.bool
};

function FluentParticipantOverlayContent({
  people,
  hubChannel,
  isOpen,
  onMute,
  onModeratePeerMedia,
  selectPerson,
  closeParticipants,
  agents,
  mySessionId,
  canOpenChat
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const intl = useIntl();
  const normalizedSearchQuery = useMemo(() => normalizeParticipantSearchQuery(searchQuery), [searchQuery]);
  const isSearchActive = normalizedSearchQuery !== "";
  const participantSearchResult = useMemo(
    () => createParticipantSearchResult(people, normalizedSearchQuery),
    [people, normalizedSearchQuery]
  );
  const agentSearchResult = useMemo(
    () => createAgentSearchResult(agents, normalizedSearchQuery),
    [agents, normalizedSearchQuery]
  );
  const filteredPeople = isSearchActive ? participantSearchResult.people : people;
  const filteredAgents = isSearchActive ? agentSearchResult.agents : agents;

  const items = useMemo(() => {
    if (isSearchActive) {
      return [];
    }

    return filteredPeople.slice(0, PARTICIPANT_AVATAR_GROUP_PREVIEW_LIMIT).map(person => ({
      id: person?.id,
      name: person?.profile?.displayName,
      image: person?.profile?.thumbnail
    }));
  }, [filteredPeople, isSearchActive]);
  const { mutedStates, updatePersonMuted } = useAllAvatarVolume(filteredPeople);

  const { isOpenChat, myMessages, openChatUser, openChatUserAll, readAllMesages, selectedUserChat } =
    useSocialContext();
  const { cameraMediaStreamsByOwnerId, screenShares } = useParticipantLayoutDialogMediaState({ mySessionId });
  const screenShareOwnerIds = useMemo(
    () => new Set(screenShares.map(screenShare => screenShare.ownerId)),
    [screenShares]
  );
  const participantChatStatusByUserId = useMemo(
    () => createParticipantChatStatusByUserId(myMessages, mySessionId, filteredPeople),
    [filteredPeople, myMessages, mySessionId]
  );
  const participantScrollRef = useRef(null);
  const selectedUserChatId = selectedUserChat?.id;
  const selectedUserChatIsAi = !!selectedUserChat?.isAi;
  const selectedParticipantChatStatus =
    selectedUserChatId && !selectedUserChatIsAi ? participantChatStatusByUserId.get(selectedUserChatId) : null;
  const selectedParticipantChatUnreadCount = selectedParticipantChatStatus?.unreadMessagesCount;

  useEffect(() => {
    if (!isOpenChat || !selectedUserChatId || selectedUserChatIsAi || !selectedParticipantChatUnreadCount) {
      return;
    }

    readAllMesages(selectedUserChatId);
  }, [isOpenChat, readAllMesages, selectedParticipantChatUnreadCount, selectedUserChatId, selectedUserChatIsAi]);

  const styles = useStyles();

  const userOwner = useMemo(() => filteredPeople.filter(person => person.roles.owner), [filteredPeople]);
  const guestPeople = useMemo(() => filteredPeople.filter(person => !person.roles.owner), [filteredPeople]);
  const hasVisibleAllUsersSection = !isSearchActive && filteredPeople.length !== 0;
  const hasVisibleAgentsSection = filteredAgents.length !== 0;
  const hasVisibleHostsSection = userOwner.length !== 0;
  const hasVisibleGuestsSection = guestPeople.length !== 0;
  const hasVisibleSearchResults = filteredPeople.length !== 0 || filteredAgents.length !== 0;
  const showHostsDivider = hasVisibleHostsSection && (hasVisibleAgentsSection || hasVisibleAllUsersSection);
  const showGuestsDivider =
    hasVisibleGuestsSection && (hasVisibleAgentsSection || hasVisibleAllUsersSection || hasVisibleHostsSection);
  const participantRows = useMemo(() => {
    const rows = [];

    if (!hasVisibleSearchResults) {
      rows.push({
        height: PARTICIPANT_SECTION_HEADER_HEIGHT,
        key: "not-found",
        type: "not-found"
      });
    }

    if (hasVisibleAgentsSection) {
      rows.push({
        height: PARTICIPANT_SECTION_HEADER_HEIGHT,
        key: "agents-header",
        label: "AI Agent",
        type: "section-header"
      });

      filteredAgents.forEach((agent, index) => {
        rows.push({
          agent,
          height: PARTICIPANT_ROW_HEIGHT,
          key: `ai-participant-${agent?.id || agent?.name || index}`,
          type: "agent"
        });
      });
    }

    if (hasVisibleAllUsersSection) {
      rows.push({
        height: PARTICIPANT_SECTION_HEADER_HEIGHT,
        key: "all-users-header",
        label: "All Users",
        type: "section-header"
      });
      rows.push({
        height: PARTICIPANT_ROW_HEIGHT,
        key: "all-users",
        type: "all-users"
      });
    }

    if (hasVisibleHostsSection) {
      if (showHostsDivider) {
        rows.push({
          height: PARTICIPANT_DIVIDER_HEIGHT,
          key: "hosts-divider",
          type: "divider"
        });
      }

      rows.push({
        height: PARTICIPANT_SECTION_HEADER_HEIGHT,
        key: "hosts-header",
        label: "Hosts",
        type: "section-header"
      });

      userOwner.forEach(person => {
        rows.push({
          height: PARTICIPANT_ROW_HEIGHT,
          key: `person-${person.id}`,
          person,
          type: "participant"
        });
      });
    }

    if (hasVisibleGuestsSection) {
      if (showGuestsDivider) {
        rows.push({
          height: PARTICIPANT_DIVIDER_HEIGHT,
          key: "guests-divider",
          type: "divider"
        });
      }

      rows.push({
        height: PARTICIPANT_SECTION_HEADER_HEIGHT,
        key: "guests-header",
        label: "Guests",
        type: "section-header"
      });

      guestPeople.forEach(person => {
        rows.push({
          height: PARTICIPANT_ROW_HEIGHT,
          key: `person-${person.id}`,
          person,
          type: "participant"
        });
      });
    }

    return rows;
  }, [
    filteredAgents,
    guestPeople,
    hasVisibleAgentsSection,
    hasVisibleAllUsersSection,
    hasVisibleGuestsSection,
    hasVisibleHostsSection,
    hasVisibleSearchResults,
    showGuestsDivider,
    showHostsDivider,
    userOwner
  ]);

  const isMod = useRole("owner");

  const canUpdateAllSettings = isMod;
  const canModerateMedia = hubChannel?.canOrWillIfCreator
    ? hubChannel.canOrWillIfCreator("mute_users")
    : !!hubChannel?.can?.("mute_users");

  const breakpoint = useBreakpoints();
  const isMobile = breakpoint === "sm" || breakpoint === "xs";
  const handleSearchQueryChange = useCallback(nextSearchQuery => setSearchQuery(nextSearchQuery), []);
  const handleOpenChange = useCallback(
    (_, { open }) => {
      if (!open) {
        closeParticipants();
      }
    },
    [closeParticipants]
  );

  const handleModeratePeerMedia = useCallback(
    (person, source) => {
      if (onModeratePeerMedia) {
        onModeratePeerMedia(person, source);
      }
    },
    [onModeratePeerMedia]
  );

  const renderParticipantRow = useCallback(
    row => {
      if (row.type === "not-found") {
        return (
          <section className={styles.sectionNotFound}>
            <Caption1Strong className={styles.sectionHeaderText}>
              <FormattedMessage id="people-sidebar.no-users-found" defaultMessage="No users found" />
            </Caption1Strong>
          </section>
        );
      }

      if (row.type === "section-header") {
        return (
          <div className={styles.sectionHeader}>
            <Caption1Strong className={styles.sectionHeaderText}>{row.label}</Caption1Strong>
          </div>
        );
      }

      if (row.type === "divider") {
        return <Divider className={styles.divider} />;
      }

      if (row.type === "agent") {
        return (
          <AIParticipant agent={row.agent} openChatUser={openChatUser} canOpenChat={canOpenChat} styles={styles} />
        );
      }

      if (row.type === "all-users") {
        return (
          <div className={styles.sectionContent}>
            <AvatarGroup layout="stack">
              {items.length > 0 && (
                <>
                  {items.map(item => (
                    <AvatarGroupItem
                      className={styles.avatarGroupItem}
                      key={item?.id}
                      image={{ src: item.image }}
                      color={"anchor"}
                    />
                  ))}
                </>
              )}
            </AvatarGroup>
            <div className={styles.icons}>
              {canOpenChat && <ChatMultiple20Filled onClick={() => openChatUserAll()} />}
              {canUpdateAllSettings && <Settings20Filled onClick={() => selectPerson()} />}
            </div>
          </div>
        );
      }

      if (row.type === "participant") {
        const person = row.person;
        const participantChatStatus = participantChatStatusByUserId.get(person.id);

        return (
          <PersonaParticipant
            openChatUser={openChatUser}
            canOpenChat={canOpenChat}
            canUpdateRoles={canModerateMedia}
            person={person}
            styles={styles}
            onMute={onMute}
            onModeratePeerMedia={handleModeratePeerMedia}
            hasCameraShare={Boolean(cameraMediaStreamsByOwnerId?.[person.id])}
            hasScreenShare={screenShareOwnerIds.has(person.id)}
            canShareCamera={canParticipantShareCamera(person)}
            canShareScreen={canParticipantShareScreen(person)}
            isLocallyMuted={Boolean(mutedStates[person.id])}
            latestPrivateMessage={participantChatStatus?.latestMessage}
            updateLocalMuted={muted => updatePersonMuted(person.id, muted)}
            unreadMessagesCount={participantChatStatus?.unreadMessagesCount || ""}
            selectPerson={selectPerson}
          />
        );
      }

      return null;
    },
    [
      canModerateMedia,
      canOpenChat,
      canUpdateAllSettings,
      cameraMediaStreamsByOwnerId,
      handleModeratePeerMedia,
      items,
      mutedStates,
      onMute,
      openChatUser,
      openChatUserAll,
      participantChatStatusByUserId,
      screenShareOwnerIds,
      selectPerson,
      styles,
      updatePersonMuted
    ]
  );

  return (
    <ResponsiveOverlayDrawer
      size={"full"}
      desktopPosition="start"
      className={mergeClasses(!isMobile && styles.drawer, !isMobile && isOpenChat && styles.splitOverlay)}
      modalType="non-modal"
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <DrawerHeader className={styles.headerDrawer}>
        <DrawerHeaderTitle
          style={{ width: "100%" }}
          action={
            <Button
              appearance="subtle"
              aria-label={intl.formatMessage({
                id: "people-sidebar.close",
                defaultMessage: "Close"
              })}
              icon={<Dismiss24Regular />}
              onClick={closeParticipants}
            />
          }
        >
          <FormattedMessage
            id="people-sidebar.title"
            defaultMessage="Participants ({numPeople})"
            values={{ numPeople: people.length }}
          />
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody
        className={styles.drawerBody}
        ref={participantScrollRef}
        style={isOpenChat ? { maxHeight: "calc(50vh - 70px)" } : {}}
      >
        <ParticipantSearchBox className={styles.search} onSearchQueryChange={handleSearchQueryChange} />
        <VirtualParticipantsList
          rows={participantRows}
          renderRow={renderParticipantRow}
          scrollContainerRef={participantScrollRef}
          styles={styles}
        />
      </DrawerBody>

      <div id="mount-chat"></div>
    </ResponsiveOverlayDrawer>
  );
}

FluentParticipantOverlayContent.propTypes = {
  agents: PropTypes.arrayOf(agentPropType).isRequired,
  canOpenChat: PropTypes.bool,
  closeParticipants: PropTypes.func.isRequired,
  hubChannel: PropTypes.shape({
    can: PropTypes.func,
    canOrWillIfCreator: PropTypes.func
  }),
  isOpen: PropTypes.bool,
  mySessionId: PropTypes.string,
  onModeratePeerMedia: PropTypes.func,
  onMute: PropTypes.func.isRequired,
  people: PropTypes.arrayOf(participantPropType).isRequired,
  selectPerson: PropTypes.func.isRequired
};

function getListViewport(scrollElement, listElement) {
  if (!scrollElement || !listElement) {
    return {
      scrollTop: 0,
      viewportHeight: PARTICIPANT_LIST_DEFAULT_VIEWPORT_HEIGHT
    };
  }

  if (scrollElement === listElement) {
    return {
      scrollTop: scrollElement.scrollTop,
      viewportHeight: scrollElement.clientHeight || PARTICIPANT_LIST_DEFAULT_VIEWPORT_HEIGHT
    };
  }

  const scrollRect = scrollElement.getBoundingClientRect?.();
  const listRect = listElement.getBoundingClientRect?.();
  const listTop =
    scrollRect && listRect ? listRect.top - scrollRect.top + scrollElement.scrollTop : listElement.offsetTop || 0;
  const visibleTop = Math.max(0, scrollElement.scrollTop - listTop);
  const visibleBottom = Math.max(visibleTop, scrollElement.scrollTop + scrollElement.clientHeight - listTop);

  return {
    scrollTop: visibleTop,
    viewportHeight: visibleBottom - visibleTop || PARTICIPANT_LIST_DEFAULT_VIEWPORT_HEIGHT
  };
}

function VirtualParticipantsList({ rows, renderRow, scrollContainerRef, styles }) {
  const listRef = useRef(null);
  const scrollFrameRef = useRef(null);
  const [viewport, setViewport] = useState({
    scrollTop: 0,
    viewportHeight: PARTICIPANT_LIST_DEFAULT_VIEWPORT_HEIGHT
  });
  const itemHeights = useMemo(() => rows.map(row => row.height), [rows]);
  const virtualWindow = useMemo(
    () =>
      getVirtualListWindow({
        itemHeights,
        overscan: PARTICIPANT_LIST_OVERSCAN,
        scrollTop: viewport.scrollTop,
        viewportHeight: viewport.viewportHeight
      }),
    [itemHeights, viewport.scrollTop, viewport.viewportHeight]
  );

  const syncViewport = useCallback(() => {
    const listElement = listRef.current;
    const scrollElement = scrollContainerRef?.current || listElement;
    const { scrollTop, viewportHeight } = getListViewport(scrollElement, listElement);

    setViewport(previous =>
      previous.scrollTop === scrollTop && previous.viewportHeight === viewportHeight
        ? previous
        : { scrollTop, viewportHeight }
    );
  }, [scrollContainerRef]);

  const handleScroll = useCallback(() => {
    if (scrollFrameRef.current !== null) {
      return;
    }

    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      syncViewport();
    });
  }, [syncViewport]);

  useEffect(() => {
    const listElement = listRef.current;
    const scrollElement = scrollContainerRef?.current || listElement;
    const handleResize = () => syncViewport();
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && scrollElement ? new ResizeObserver(() => syncViewport()) : null;

    syncViewport();

    scrollElement?.addEventListener?.("scroll", handleScroll);

    if (resizeObserver) {
      resizeObserver.observe(scrollElement);
    }

    window.addEventListener("resize", handleResize);

    return () => {
      scrollElement?.removeEventListener?.("scroll", handleScroll);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      window.removeEventListener("resize", handleResize);

      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }
    };
  }, [handleScroll, rows.length, scrollContainerRef, syncViewport]);

  return (
    <div className={styles.contentParticipants} ref={listRef}>
      <div className={styles.virtualListSpacer} style={{ height: `${virtualWindow.totalHeight}px` }}>
        {virtualWindow.items.map(item => {
          const row = rows[item.index];

          if (!row) {
            return null;
          }

          return (
            <div
              className={styles.virtualRow}
              key={row.key}
              style={{ height: `${item.height}px`, transform: `translateY(${item.offset}px)` }}
            >
              {renderRow(row)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

VirtualParticipantsList.propTypes = {
  renderRow: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      height: PropTypes.number.isRequired,
      key: PropTypes.string.isRequired
    })
  ).isRequired,
  scrollContainerRef: PropTypes.shape({
    current: PropTypes.object
  }),
  styles: PropTypes.object.isRequired
};

const ParticipantSearchBox = React.memo(function ParticipantSearchBox({ className, onSearchQueryChange }) {
  const [value, setValue] = useState("");
  const searchInputRef = useRef(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => onSearchQueryChange(value), PARTICIPANT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [onSearchQueryChange, value]);

  useEffect(() => {
    const input = searchInputRef.current;
    if (!input) return undefined;

    const stopPropagation = event => event.stopPropagation();
    input.addEventListener("keydown", stopPropagation);
    input.addEventListener("keypress", stopPropagation);
    input.addEventListener("keyup", stopPropagation);

    return () => {
      input.removeEventListener("keydown", stopPropagation);
      input.removeEventListener("keypress", stopPropagation);
      input.removeEventListener("keyup", stopPropagation);
    };
  }, []);

  return (
    <SearchBox
      ref={searchInputRef}
      value={value}
      onChange={(_event, data) => setValue(data.value)}
      placeholder="Search"
      appearance="filled-lighter"
      className={className}
    />
  );
});

ParticipantSearchBox.propTypes = {
  className: PropTypes.string,
  onSearchQueryChange: PropTypes.func.isRequired
};

function AIParticipant({ styles, agent, openChatUser, canOpenChat }) {
  const textChatEnabled = !isWebRtcDisabled() && !!configManager.config.textChat?.enabled;
  const canOpenAgentChat = canOpenChat && shouldShowAgentChat({ textChatEnabled, provider: agent?.provider });
  return (
    <div className={styles.sectionContent}>
      <Persona
        className={styles.persona}
        name={`${agent?.name}`}
        avatar={{
          color: "colorful"
        }}
      />
      <div className={styles.icons}>
        {canOpenAgentChat && <ChatMultiple20Filled onClick={() => openChatUser(agent)} />}
      </div>
    </div>
  );
}

AIParticipant.propTypes = {
  agent: agentPropType.isRequired,
  canOpenChat: PropTypes.bool,
  openChatUser: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired
};

function PersonaParticipant({
  canUpdateRoles,
  styles,
  person,
  onMute,
  onModeratePeerMedia,
  hasCameraShare,
  hasScreenShare,
  canShareCamera,
  canShareScreen,
  isLocallyMuted,
  latestPrivateMessage,
  updateLocalMuted,
  unreadMessagesCount,
  selectPerson,
  openChatUser,
  canOpenChat
}) {
  const intl = useIntl();

  const isMe = person.isMe;
  const voiceChatEnabled = configManager.config.voiceChat.enabled;
  const isVoiceActive = voiceChatEnabled && person.micPresence?.muted === false;
  const canMuteVoice = (canUpdateRoles || isMe) && isVoiceActive && person?.presence != "lobby";
  const VoiceIcon = isVoiceActive ? Mic20Filled : MicOff20Filled;
  const classNameVoiceIcon = isVoiceActive ? "" : styles.redIcon;
  const SpeakerIcon = isLocallyMuted ? SpeakerMute20Filled : Speaker220Filled;
  const speakerTooltipText = isLocallyMuted ? "Unmute for me" : "Mute for me";

  const textChatEnabled = !isWebRtcDisabled() && !!configManager.config?.textChat?.enabled;
  const canOpenParticipantChat =
    canOpenChat &&
    canShowParticipantDialogChat({
      textChatEnabled,
      entered: true,
      hasDialogConnectedOnce: true,
      person
    });
  const canModerateRemoteMedia = canUpdateRoles && !!onModeratePeerMedia && !isMe && person?.presence != "lobby";

  return (
    <div className={styles.sectionContent}>
      <Persona
        className={styles.persona}
        name={`${person?.profile?.displayName} ${isMe ? "(You)" : ""}`}
        avatar={{
          image: {
            src: person?.profile?.thumbnail
          }
        }}
        presence={
          person?.presence != "lobby"
            ? {
                status: "available"
              }
            : {
                icon: <ClockFilled style={{ width: "12px", height: "12px" }} color="#EAA300" />
              }
        }
      />
      <div className={styles.icons}>
        {canOpenParticipantChat && latestPrivateMessage && unreadMessagesCount && (
          <CounterBadge count={unreadMessagesCount} />
        )}
        {canOpenParticipantChat && <ChatMultiple20Filled onClick={() => openChatUser(person)} />}
        {(canUpdateRoles || isMe) && person?.presence != "lobby" && (
          <VoiceIcon
            style={!canMuteVoice ? { pointerEvents: "none" } : {}}
            className={classNameVoiceIcon}
            onClick={canMuteVoice ? () => onMute(person) : undefined}
          />
        )}
        {canModerateRemoteMedia && (canShareCamera || hasCameraShare) && (
          <MediaModerationButton
            active={hasCameraShare}
            activeIcon={Video20Filled}
            inactiveIcon={VideoOff20Filled}
            label={intl.formatMessage({
              id: "people-sidebar.stop-camera",
              defaultMessage: "Stop camera"
            })}
            onClick={() => onModeratePeerMedia(person, MediaDevices.CAMERA)}
            styles={styles}
          />
        )}
        {canModerateRemoteMedia && (canShareScreen || hasScreenShare) && (
          <MediaModerationButton
            active={hasScreenShare}
            activeIcon={ShareScreenStart20Filled}
            inactiveIcon={ShareScreenOffIcon}
            label={intl.formatMessage({
              id: "people-sidebar.stop-screen-share",
              defaultMessage: "Stop screen share"
            })}
            onClick={() => onModeratePeerMedia(person, MediaDevices.SCREEN)}
            styles={styles}
          />
        )}
        {!isMe && person?.presence != "lobby" && (
          <Tooltip content={speakerTooltipText} relationship="description">
            <SpeakerIcon
              style={!voiceChatEnabled ? { pointerEvents: "none" } : { cursor: "pointer" }}
              className={isLocallyMuted ? styles.redIcon : ""}
              onClick={() => updateLocalMuted(!isLocallyMuted)}
            />
          </Tooltip>
        )}
        {<Settings20Filled onClick={() => selectPerson(person)} />}
      </div>
    </div>
  );
}

PersonaParticipant.propTypes = {
  canOpenChat: PropTypes.bool,
  canShareCamera: PropTypes.bool,
  canShareScreen: PropTypes.bool,
  canUpdateRoles: PropTypes.bool,
  hasCameraShare: PropTypes.bool,
  hasScreenShare: PropTypes.bool,
  isLocallyMuted: PropTypes.bool,
  latestPrivateMessage: PropTypes.shape({
    messages: PropTypes.arrayOf(
      PropTypes.shape({
        read: PropTypes.bool
      })
    )
  }),
  onModeratePeerMedia: PropTypes.func,
  onMute: PropTypes.func.isRequired,
  openChatUser: PropTypes.func.isRequired,
  person: participantPropType.isRequired,
  selectPerson: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired,
  unreadMessagesCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  updateLocalMuted: PropTypes.func.isRequired
};

function MediaModerationButton({ active, activeIcon, inactiveIcon, label, onClick, styles }) {
  const Icon = active ? activeIcon : inactiveIcon;
  const className = active
    ? styles.mediaModerationButton
    : `${styles.mediaModerationButton} ${styles.mediaModerationButtonDisabled}`;

  return (
    <Tooltip content={label} relationship="description">
      <button aria-label={label} className={className} disabled={!active} onClick={onClick} type="button">
        <Icon className={active ? "" : styles.redIcon} />
      </button>
    </Tooltip>
  );
}

MediaModerationButton.propTypes = {
  active: PropTypes.bool,
  activeIcon: PropTypes.elementType.isRequired,
  inactiveIcon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired
};
